/**
 * reCAPTCHA v3 (invisible) — carga perezosa del script + ejecución por acción.
 *
 * El script se inyecta una sola vez, sólo cuando hay site key configurada. La
 * verificación real la hace el backend (Recaptcha::verify). Si no hay site key o
 * el script/ejecución falla, executeRecaptcha devuelve `undefined` y el caller
 * manda el login/registro SIN captcha_token (el backend lo acepta si el feature
 * está off; si está on, rechaza — fail-closed del lado servidor).
 */

interface GrecaptchaV3 {
  ready: (cb: () => void) => void;
  execute: (siteKey: string, opts: { action: string }) => Promise<string>;
}

declare global {
  interface Window {
    grecaptcha?: GrecaptchaV3;
  }
}

let scriptPromise: Promise<boolean> | null = null;

/**
 * Precarga el script de reCAPTCHA apenas se abre la pantalla (login/registro),
 * para que el token ya esté listo al enviar y el usuario NO tenga que esperar la
 * carga en el submit. No-op si no hay site key.
 */
export function preloadRecaptcha(siteKey: string | undefined): void {
  if (!siteKey) return;
  void loadScript(siteKey);
}

/** Inyecta (una vez) el script de reCAPTCHA v3 para la site key dada. */
function loadScript(siteKey: string): Promise<boolean> {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<boolean>((resolve) => {
    if (window.grecaptcha?.execute) {
      resolve(true);
      return;
    }
    const s = document.createElement('script');
    s.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    // Timeout defensivo: si el script no carga ni falla (p.ej. WebView sin acceso
    // a google.com), NO dejamos colgado al caller — resolvemos false a los 6 s.
    setTimeout(() => resolve(false), 6000);
    document.head.appendChild(s);
  });
  return scriptPromise;
}

/**
 * Ejecuta reCAPTCHA v3 para la acción dada y devuelve el token, o `undefined`
 * si no hay site key / el script no está disponible / falla o TARDA demasiado.
 *
 * Timeout duro: `grecaptcha.execute()` puede colgar sin resolver ni rechazar
 * (típico cuando el dominio NO está registrado, como en un WebView Capacitor).
 * Sin este límite, el botón de submit quedaría "cargando" para siempre. Con él,
 * a los 6 s devolvemos undefined y el submit sigue (en nativo el backend saltea
 * el captcha; en web, cae a la verificación fail-closed del servidor).
 */
export async function executeRecaptcha(
  siteKey: string | undefined,
  action: string,
): Promise<string | undefined> {
  if (!siteKey) return undefined;
  try {
    const ok = await loadScript(siteKey);
    const g = window.grecaptcha;
    if (!ok || !g?.execute) return undefined;

    const token = await Promise.race<string | undefined>([
      (async () => {
        await new Promise<void>((res) => g.ready(() => res()));
        return await g.execute(siteKey, { action });
      })(),
      new Promise<undefined>((res) => setTimeout(() => res(undefined), 6000)),
    ]);

    return token ?? undefined;
  } catch {
    return undefined;
  }
}
